import { Card, CardTitle } from "@/components/Card";
import type { QualificationQuestionDocument } from "@/lib/types";
import {
  ChipListField,
  RowField,
  RowListField,
  RowTextInput,
  SelectField,
} from "@/components/strategy/FieldEditors";
import { usePhaseEditor, type PhaseEditorProps } from "@/components/strategy/phaseEditing";

const TYPES = ["text", "number", "boolean", "enum"] as const;
const KEY_PATTERN = /^[a-z0-9_-]{1,64}$/;

/**
 * The open questions the agent works into its emails while a prospect is in
 * this phase, and extracts answers for from each reply. The key is the handle
 * a transition tests as `qualification.<key>`.
 */
export default function PhaseQuestionsCard(props: PhaseEditorProps) {
  const { list, edit, isEdited } = usePhaseEditor(props);
  const questions = list<QualificationQuestionDocument>(["qualification_questions"]);

  return (
    <Card>
      <div className="flex items-center gap-1.5">
        <CardTitle>Qualification questions</CardTitle>
        {isEdited(["qualification_questions"]) ? (
          <span title="unsaved change" className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
        ) : null}
      </div>
      <p className="mb-2 text-xs text-[var(--fg-muted)]">
        Asked naturally in the agent's emails until answered; each reply is read for the
        answers. Transitions can test an answer as{" "}
        <code>qualification.&lt;key&gt;</code>.
      </p>
      <RowListField<QualificationQuestionDocument>
        value={questions}
        rowLabel="question"
        addLabel="Add question"
        newRow={() => ({ key: "", prompt: "", type: "text", required: false })}
        onCommit={(rows) => edit(["qualification_questions"], rows)}
        renderRow={(row, update, i) => {
          const badKey = typeof row.key === "string" && row.key !== "" && !KEY_PATTERN.test(row.key);
          return (
            <>
              <div className="grid grid-cols-[1fr_auto] gap-2">
                <RowField label="key">
                  <RowTextInput
                    ariaLabel={`Question ${i + 1} key`}
                    value={row.key}
                    mono
                    placeholder="e.g. budget"
                    onCommit={(v) => update({ ...row, key: v.trim() })}
                  />
                  {badKey ? (
                    <span className="text-[10px] text-red-700">
                      lower-case letters, digits, _ or - only
                    </span>
                  ) : null}
                </RowField>
                <RowField label="answer type">
                  <SelectField
                    ariaLabel={`Question ${i + 1} type`}
                    value={row.type ?? "text"}
                    options={TYPES.map((t) => ({ value: t, label: t }))}
                    onCommit={(v) => {
                      const next = { ...row, type: (v ?? "text") as QualificationQuestionDocument["type"] };
                      if (next.type !== "enum") delete next.options;
                      update(next);
                    }}
                  />
                </RowField>
              </div>
              <RowField label="question">
                <RowTextInput
                  ariaLabel={`Question ${i + 1} prompt`}
                  value={row.prompt}
                  multiline
                  placeholder="What the agent needs to find out"
                  onCommit={(v) => update({ ...row, prompt: v })}
                />
              </RowField>
              {row.type === "enum" ? (
                <RowField label="allowed answers">
                  <ChipListField
                    addLabel={`Question ${i + 1} option`}
                    value={row.options}
                    onCommit={(v) => {
                      const next = { ...row };
                      if (v === null) delete next.options;
                      else next.options = v;
                      update(next);
                    }}
                  />
                </RowField>
              ) : null}
              <label className="flex items-center gap-1.5">
                <input
                  type="checkbox"
                  aria-label={`Question ${i + 1} required`}
                  checked={row.required === true}
                  onChange={(e) => update({ ...row, required: e.target.checked })}
                  className="h-3.5 w-3.5"
                />
                must be answered before the phase counts as qualified
              </label>
            </>
          );
        }}
      />
    </Card>
  );
}
